/**
 * Shared WebAuthn browser-API helpers.
 *
 * Low-level plumbing shared by every passkey flow (self-service enrollment,
 * passkey login, admin enrollment, and first-login passkey setup):
 *  - browser support detection
 *  - base64url ↔ ArrayBuffer conversion
 *  - shaping server options for navigator.credentials.create/get
 *  - serializing the browser's credential response for the backend
 *
 * Keeping these in one module avoids duplicating the (easy-to-get-wrong)
 * byte-encoding logic across services/passkeys.ts and services/mfaSetup.ts.
 */

// ---------------------------------------------------------------------------
// Browser support check
// ---------------------------------------------------------------------------

export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials !== 'undefined' &&
    typeof navigator.credentials.create === 'function' &&
    typeof navigator.credentials.get === 'function'
  )
}

/**
 * True if the browser supports conditional mediation ("passkey autofill") — the
 * browser can surface a saved passkey in the email/username field's autofill
 * dropdown. Additive: when unavailable, the explicit "Sign in with a passkey"
 * button still works. Returns a Promise because the platform API is async.
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  try {
    const pkc = window.PublicKeyCredential as unknown as {
      isConditionalMediationAvailable?: () => Promise<boolean>
    }
    if (!isPasskeySupported() || typeof pkc?.isConditionalMediationAvailable !== 'function') {
      return false
    }
    return await pkc.isConditionalMediationAvailable()
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Base64url ↔ ArrayBuffer helpers (required by WebAuthn browser API)
// ---------------------------------------------------------------------------

export function b64urlToBytes(b64url: string): Uint8Array {
  // Replace URL-safe chars, add padding
  const base64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const raw = atob(padded)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

export function bytesToB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

// Convert options returned by the server into the format expected by the browser
export function prepareRegistrationOptions(
  options: Record<string, unknown>,
): PublicKeyCredentialCreationOptions {
  const opts = { ...options } as Record<string, unknown>

  // challenge: base64url → ArrayBuffer
  opts.challenge = b64urlToBytes(opts.challenge as string)

  // user.id: base64url → ArrayBuffer
  const user = { ...(opts.user as Record<string, unknown>) }
  user.id = b64urlToBytes(user.id as string)
  opts.user = user

  // excludeCredentials[].id: base64url → ArrayBuffer
  if (Array.isArray(opts.excludeCredentials)) {
    opts.excludeCredentials = opts.excludeCredentials.map((c: Record<string, unknown>) => ({
      ...c,
      id: b64urlToBytes(c.id as string),
    }))
  }

  return opts as unknown as PublicKeyCredentialCreationOptions
}

export function prepareAuthenticationOptions(
  options: Record<string, unknown>,
): PublicKeyCredentialRequestOptions {
  const opts = { ...options } as Record<string, unknown>

  // challenge: base64url → ArrayBuffer
  opts.challenge = b64urlToBytes(opts.challenge as string)

  // allowCredentials[].id: base64url → ArrayBuffer
  if (Array.isArray(opts.allowCredentials)) {
    opts.allowCredentials = opts.allowCredentials.map((c: Record<string, unknown>) => ({
      ...c,
      id: b64urlToBytes(c.id as string),
    }))
  }

  return opts as unknown as PublicKeyCredentialRequestOptions
}

// Serialize a registration credential for the backend
export function serializeRegistrationCredential(
  credential: PublicKeyCredential,
): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: bytesToB64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bytesToB64url(response.clientDataJSON),
      attestationObject: bytesToB64url(response.attestationObject),
      transports: response.getTransports ? response.getTransports() : [],
    },
  }
}

// Serialize an authentication credential for the backend
export function serializeAuthenticationCredential(
  credential: PublicKeyCredential,
): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: bytesToB64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bytesToB64url(response.authenticatorData),
      clientDataJSON: bytesToB64url(response.clientDataJSON),
      signature: bytesToB64url(response.signature),
      userHandle: response.userHandle ? bytesToB64url(response.userHandle) : null,
    },
  }
}

/**
 * Map a raw navigator.credentials error into a friendlier message,
 * translating user cancellation/abort into a stable, human-readable string.
 */
export function webauthnErrorMessage(err: unknown, cancelledMessage: string): string {
  const msg = err instanceof Error ? err.message : String(err)
  if (msg.includes('cancel') || msg.includes('abort') || msg.includes('NotAllowed')) {
    return cancelledMessage
  }
  return `Browser error: ${msg}`
}
